import { cropPixelImage } from '../image.js';
import { GLYPH_HEIGHT } from '../constants.js';
import { isAnchorPixel, textInkProfile } from '../preprocess.js';
import { setColumnBit } from './mask.js';
import { TEXT_EVIDENCE_HEIGHT, TEXT_EVIDENCE_INK_THRESHOLD, TEXT_EVIDENCE_VERSION, } from './types.js';
const DEFAULT_MAX_WIDTH = 4096;
const DEFAULT_MAX_CROP_HEIGHT = 4096;
function channels(image) {
    return image.channels ?? 4;
}
function stride(image) {
    return image.stride ?? image.width * channels(image);
}
function pixelOffset(image, x, y) {
    return y * stride(image) + x * channels(image);
}
function assertImage(image, options) {
    const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH;
    const maxCropHeight = options.maxCropHeight ?? DEFAULT_MAX_CROP_HEIGHT;
    if (!Number.isInteger(image.width) || image.width < 1 || image.width > maxWidth) {
        throw new RangeError(`crop width must be an integer in [1, ${maxWidth}]`);
    }
    if (!Number.isInteger(image.height) || image.height < GLYPH_HEIGHT || image.height > maxCropHeight) {
        throw new RangeError(`crop height must be an integer in [${GLYPH_HEIGHT}, ${maxCropHeight}]`);
    }
    const required = stride(image) * (image.height - 1) + image.width * channels(image);
    if (image.data.length < required)
        throw new RangeError('image data is smaller than width/height/channels require');
}
function detectLineY(image, profile, threshold) {
    let minY = Number.POSITIVE_INFINITY;
    for (let y = 0; y < image.height; y++) {
        for (let x = 0; x < image.width; x++) {
            const i = pixelOffset(image, x, y);
            if (profile.evidencePixel(image.data[i] ?? 0, image.data[i + 1] ?? 0, image.data[i + 2] ?? 0) >= threshold) {
                minY = y;
                break;
            }
        }
        if (minY !== Number.POSITIVE_INFINITY)
            break;
    }
    if (minY === Number.POSITIVE_INFINITY)
        return 0;
    return Math.min(image.height - GLYPH_HEIGHT, Math.max(0, minY - 1));
}
/** Client helper: reduce a known crop to exact anchors plus a 1-bit shape mask. */
export function createTextEvidence(image, options) {
    assertImage(image, options);
    const profile = textInkProfile(options.profile);
    const lineDetectionThreshold = options.lineDetectionThreshold ?? 0.18;
    if (!(lineDetectionThreshold >= 0 && lineDetectionThreshold <= 1))
        throw new RangeError('lineDetectionThreshold must be in [0, 1]');
    const lineY = options.lineY ?? detectLineY(image, profile, lineDetectionThreshold);
    if (!Number.isInteger(lineY) || lineY < 0 || lineY + GLYPH_HEIGHT > image.height) {
        throw new RangeError(`lineY must select a ${GLYPH_HEIGHT}px line inside the crop`);
    }
    const inkThreshold = options.inkThreshold ?? TEXT_EVIDENCE_INK_THRESHOLD;
    if (!(inkThreshold >= 0 && inkThreshold <= 1))
        throw new RangeError('inkThreshold must be in [0, 1]');
    const anchorColumns = new Uint16Array(image.width);
    const inkColumns = new Uint16Array(image.width);
    for (let y = 0; y < GLYPH_HEIGHT; y++) {
        for (let x = 0; x < image.width; x++) {
            const i = pixelOffset(image, x, lineY + y);
            const r = image.data[i] ?? 0;
            const g = image.data[i + 1] ?? 0;
            const b = image.data[i + 2] ?? 0;
            if (isAnchorPixel(profile, r, g, b))
                setColumnBit(anchorColumns, x, y);
            if (profile.evidencePixel(r, g, b) >= inkThreshold)
                setColumnBit(inkColumns, x, y);
        }
    }
    return {
        version: TEXT_EVIDENCE_VERSION,
        width: image.width,
        cropHeight: image.height,
        lineY,
        anchorColumns,
        inkColumns,
    };
}
export function createTextCropEvidence(source, rect, options) {
    return createTextEvidence(cropPixelImage(source, rect), options);
}
/** Reuse the decoder's already-built line to avoid reading crop pixels twice. */
export function createTextEvidenceFromLine(line, cropHeight, lineY, options = {}) {
    const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH;
    const maxCropHeight = options.maxCropHeight ?? DEFAULT_MAX_CROP_HEIGHT;
    if (!Number.isInteger(line.width) || line.width < 1 || line.width > maxWidth)
        throw new RangeError('invalid line width');
    if (!Number.isInteger(cropHeight) || cropHeight < GLYPH_HEIGHT || cropHeight > maxCropHeight)
        throw new RangeError('invalid cropHeight');
    if (!Number.isInteger(lineY) || lineY < 0 || lineY + GLYPH_HEIGHT > cropHeight)
        throw new RangeError('invalid lineY');
    const inkThreshold = options.inkThreshold ?? TEXT_EVIDENCE_INK_THRESHOLD;
    if (!(inkThreshold >= 0 && inkThreshold <= 1))
        throw new RangeError('inkThreshold must be in [0, 1]');
    const anchorColumns = Uint16Array.from(line.columns);
    const inkColumns = new Uint16Array(line.width);
    for (let y = 0; y < TEXT_EVIDENCE_HEIGHT; y++) {
        const row = y * line.width;
        for (let x = 0; x < line.width; x++) {
            if ((line.evidence[row + x] ?? 0) >= inkThreshold)
                setColumnBit(inkColumns, x, y);
        }
    }
    return {
        version: TEXT_EVIDENCE_VERSION,
        width: line.width,
        cropHeight,
        lineY,
        anchorColumns,
        inkColumns,
    };
}
//# sourceMappingURL=client.js.map